import {
	Component,
	Input,
	WritableSignal,
	signal,
	ChangeDetectionStrategy,
	inject,
	output,
	OutputEmitterRef
} from '@angular/core';
import { UntypedFormGroup, FormControl, Validators, UntypedFormControl, AbstractControl } from '@angular/forms';
import { AutocompleteOption } from '@app/shared/ui/components/inputs/autocomplete/autocomplete.component';
import { TranslateService } from '@ngx-translate/core';
import { TaskReminder } from '@app/pages/tasks/models/tasks.model';
import {
	tasksRemindersOccurrenceTypesOptionsCallback,
	tasksRemindersInAppTypesOptionsCallback,
	activeUsersOptionsCallback
} from '@app/shared/ui/components/inputs/autocomplete/autocomplete.helper';
import {
	mapParamsToRequest,
	TASK_REMINDER_FIELD_TO_TYPE_MAP
} from '@app/pages/clients-and-matters/helpers/clients-and-matters.helper';
import { getDirtyFields } from '@app/pages/tasks/helper/tasks.helper';
import { Params } from '@angular/router';
import {
	ReminderInAppTypes,
	ReminderOccurrenceTypes,
	ReminderRecurringTypes,
	ReminderActionsEnum
} from '@app/pages/tasks/enums/tasks.enums';
import { ReminderOccurrenceType, ReminderInAppType } from '@app/core/services/settings/settings.model';
import { mapEntityToAutocompleteOptionV2, mapEntitiesToAutocompleteOptionsV2 } from '@app/core/helpers/helpers.helper';
import { Languages, hour24Format, MM_DD_YYYY, DD_MM_YYYY, dateFormat, DropDownOption } from '@core';
import { SettingsService } from '@app/core/services/settings/settings.service';
import { format } from 'date-fns';
import { getSelectedDateFromString } from '@app/services/helpers/helpers.helper';
import { FreeTextInputType } from '@app/shared/ui/components/inputs/free-text/free-text-input.component';
import { map } from 'rxjs/operators';
import { positiveIntegerValidator } from '@app/services/helpers/custom-validators.validators';

const DEFAULT_REMINDER_RECURRING_OPTION: number = 0;
const DEFAULT_INTERVAL_ONE_TIME_REMINDER: number = 1;

@Component({
	selector: 'task-reminder-form',
	templateUrl: './task-reminder-form.component.html',
	styleUrls: ['./task-reminder-form.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false
})
export class TaskReminderFormComponent {
	private translateService = inject(TranslateService);
	private settingsService = inject(SettingsService);

	@Input() set reminder(reminder: TaskReminder) {
		if (reminder) {
			this.mapReminderToForm(reminder);
		}
	}
	readonly onSubmit: OutputEmitterRef<Params> = output<Params>();
	readonly onClose: OutputEmitterRef<ReminderActionsEnum> = output<ReminderActionsEnum>();

	private readonly currentLang: Languages;
	private readonly dateFormat: string;

	public isOneTimeReminder: WritableSignal<boolean> = signal<boolean>(true);
	public readonly translation: { [key: string]: any } = this.translateService.instant('MODALS.TASK.REMINDERS');
	public readonly tasksRemindersOccurrenceTypesOptionsCallback = searchTerm => {
		return tasksRemindersOccurrenceTypesOptionsCallback(searchTerm).pipe(
			map((options: AutocompleteOption[]) => options.filter(option => option.id !== ReminderOccurrenceTypes.OneTime))
		);
	};
	public readonly tasksRemindersInAppTypesOptionsCallback = tasksRemindersInAppTypesOptionsCallback;
	public readonly usersOptionsCallback = activeUsersOptionsCallback;
	public readonly inputType = FreeTextInputType;
	public readonly reminderActionsEnum = ReminderActionsEnum;

	public recurringOptions: DropDownOption[] = [
		{ label: this.translation.RECURRING_OPTIONS.ONE_TIME, value: ReminderRecurringTypes.OneTime },
		{ label: this.translation.RECURRING_OPTIONS.RECURRING, value: ReminderRecurringTypes.Recurring }
	];
	public readonly REMINDER_DATE_START_CONTROL: string = 'dateStart';
	public readonly REMINDER_TIME_START_CONTROL: string = 'timeStart';
	public readonly REMINDER_DATE_END_CONTROL: string = 'dateEnd';
	public readonly REMINDER_TIME_END_CONTROL: string = 'timeEnd';
	public readonly REMINDER_INTERVAL_CONTROL: string = 'interval';
	public readonly REMINDER_OCCURRENCE_CONTROL: string = 'occurrence';
	public readonly REMINDER_NOTIFICATION_TYPE_CONTROL: string = 'notificationType';
	public readonly REMINDER_NOTES_CONTROL: string = 'details';
	public readonly REMINDER_RECIPIENTS_CONTROL: string = 'recipients';
	public readonly RECURRING_OPTIONS_CONTROL: string = 'recurringOptions';
	public formGroup: UntypedFormGroup = new UntypedFormGroup({
		[this.REMINDER_DATE_START_CONTROL]: new FormControl<Date>({ value: null, disabled: true }),
		[this.REMINDER_TIME_START_CONTROL]: new UntypedFormControl({ value: null, disabled: true }),
		[this.REMINDER_DATE_END_CONTROL]: new FormControl<Date>(null, Validators.required),
		[this.REMINDER_TIME_END_CONTROL]: new UntypedFormControl(null, Validators.required),
		[this.REMINDER_INTERVAL_CONTROL]: new FormControl<number>(
			{ value: null, disabled: true },
			positiveIntegerValidator
		),
		[this.REMINDER_OCCURRENCE_CONTROL]: new FormControl<AutocompleteOption>({ value: null, disabled: true }),
		[this.REMINDER_NOTIFICATION_TYPE_CONTROL]: new FormControl<AutocompleteOption>(null, Validators.required),
		[this.REMINDER_NOTES_CONTROL]: new FormControl<string>(''),
		[this.REMINDER_RECIPIENTS_CONTROL]: new FormControl<AutocompleteOption>(null),
		[this.RECURRING_OPTIONS_CONTROL]: new FormControl<DropDownOption>(
			this.recurringOptions[DEFAULT_REMINDER_RECURRING_OPTION]
		)
	});

	constructor() {
		this.currentLang = this.translateService.currentLang as Languages;
		this.dateFormat = this.settingsService.isUsFirm() ? MM_DD_YYYY : DD_MM_YYYY;
	}

	private setIsOneTimeReminder(value: boolean): void {
		this.isOneTimeReminder.set(value);
	}

	private mapReminderToForm(reminder: TaskReminder): void {
		const occurrence: ReminderOccurrenceType = this.settingsService
			.getReminderOccurrenceTypes()
			.find(type => type.id === reminder.occurrence);
		const notificationType: ReminderInAppType = this.settingsService
			.getReminderInAppTypes()
			.find(type => type.id === reminder.in_app_type);
		const dateStart: Date = new Date(reminder.start_date);
		const dateEnd: Date = new Date(reminder.end_date);

		let mappedReminder: Params = {
			timeEnd: format(dateEnd, hour24Format),
			dateEnd: format(dateEnd, dateFormat),
			...this.mapReminderOccurrenceToForm(reminder, occurrence),
			notificationType: reminder.in_app_type
				? mapEntityToAutocompleteOptionV2(notificationType, this.currentLang, 'he_name')
				: null,
			recipients: reminder.recipients.length
				? mapEntitiesToAutocompleteOptionsV2(reminder.recipients, this.currentLang)
				: null,
			details: reminder.details
		};
		if (occurrence.id !== ReminderOccurrenceTypes.OneTime) {
			mappedReminder = {
				...mappedReminder,
				timeStart: format(dateStart, hour24Format),
				dateStart: format(dateStart, dateFormat)
			};
		}
		this.formGroup.patchValue(mappedReminder);
	}

	private mapReminderOccurrenceToForm(reminder: TaskReminder, occurrence: ReminderOccurrenceType): Params {
		if (occurrence.id === ReminderOccurrenceTypes.OneTime) {
			return {};
		} else {
			this.formGroup.get(this.RECURRING_OPTIONS_CONTROL).setValue(ReminderRecurringTypes.Recurring);
			this.onRecurringOptionChange(ReminderRecurringTypes.Recurring);
			return {
				interval: reminder.occurrence_interval,
				occurrence: mapEntityToAutocompleteOptionV2(occurrence, this.currentLang, 'he_name')
			};
		}
	}

	private mapTimeAndDateToRequest(timeCtrl: string, dateCtrl: string): Date {
		const time: string = this.formGroup.get(timeCtrl).value;
		const date: string = format(this.formGroup.get(dateCtrl).value, this.dateFormat);

		const [hours, minutes] = time.split(':');
		const dateAndTime: Date = getSelectedDateFromString(date, this.dateFormat);
		dateAndTime.setHours(+hours, +minutes);

		return dateAndTime;
	}

	private getNotificationTypeParams(): Params {
		//TODO: implement in future feature
		return {
			in_app_notification: true,
			in_app_type: ReminderInAppTypes.Silent
		};
	}

	private getOccurrenceParams(): Params {
		const occurrence: ReminderOccurrenceType = this.formGroup.get(this.REMINDER_OCCURRENCE_CONTROL).value;
		const interval: number = this.formGroup.get(this.REMINDER_INTERVAL_CONTROL).value;
		return this.isOneTimeReminder()
			? { occurrence: ReminderOccurrenceTypes.OneTime, occurrence_interval: DEFAULT_INTERVAL_ONE_TIME_REMINDER }
			: { occurrence: occurrence[0].id, occurrence_interval: interval };
	}

	private mapFormToParams(dirtyFields: Params): Params {
		let params: Partial<TaskReminder> = {};
		Object.keys(dirtyFields).forEach(key => {
			switch (key) {
				case this.REMINDER_DATE_START_CONTROL:
				case this.REMINDER_TIME_START_CONTROL:
					if (!this.isOneTimeReminder() && !params.start_date) {
						params.start_date = this.mapTimeAndDateToRequest(
							this.REMINDER_TIME_START_CONTROL,
							this.REMINDER_DATE_START_CONTROL
						);
					}
					break;
				case this.REMINDER_DATE_END_CONTROL:
				case this.REMINDER_TIME_END_CONTROL:
					if (!params.end_date) {
						params.end_date = this.mapTimeAndDateToRequest(
							this.REMINDER_TIME_END_CONTROL,
							this.REMINDER_DATE_END_CONTROL
						);
					}
					if (this.isOneTimeReminder()) {
						params.start_date = params.end_date;
					}
					break;
				case this.REMINDER_OCCURRENCE_CONTROL:
				case this.REMINDER_INTERVAL_CONTROL:
					if (!params.occurrence && !params.occurrence_interval) {
						params = {
							...params,
							...this.getOccurrenceParams()
						};
					}
					break;
				case this.REMINDER_NOTIFICATION_TYPE_CONTROL:
					params = {
						...params,
						...this.getNotificationTypeParams()
					};
					break;
				case this.RECURRING_OPTIONS_CONTROL:
					break;
				default:
					params[key] = dirtyFields[key];
					break;
			}
		});
		return params as Params;
	}

	private getRequestParams(): Params {
		const dirtyFields: Params = getDirtyFields(this.formGroup);
		const mappedFields: Params = this.mapFormToParams(dirtyFields);
		return mapParamsToRequest(mappedFields, TASK_REMINDER_FIELD_TO_TYPE_MAP, true);
	}

	public onAction(action: ReminderActionsEnum): void {
		switch (action) {
			case ReminderActionsEnum.Save:
				if (this.formGroup.valid) {
					const requestParams: Params = this.getRequestParams();
					this.onSubmit.emit(requestParams);
				} else {
					this.formGroup.markAllAsTouched();
				}
				break;
			case ReminderActionsEnum.Cancel:
				this.onClose.emit(action);
				break;
		}
	}

	public onRecurringOptionChange(option: ReminderRecurringTypes): void {
		const updateControls: AbstractControl[] = [
			this.formGroup.get(this.REMINDER_TIME_START_CONTROL),
			this.formGroup.get(this.REMINDER_DATE_START_CONTROL),
			this.formGroup.get(this.REMINDER_INTERVAL_CONTROL),
			this.formGroup.get(this.REMINDER_OCCURRENCE_CONTROL)
		];

		switch (option) {
			case ReminderRecurringTypes.OneTime:
				updateControls.forEach(ctrl => {
					ctrl.disable();
					ctrl.setValue(null);
					ctrl.markAsDirty();
				});
				this.setIsOneTimeReminder(true);
				break;
			case ReminderRecurringTypes.Recurring:
				updateControls.forEach(ctrl => {
					ctrl.enable();
				});
				this.setIsOneTimeReminder(false);
				break;
		}
	}
}
